import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { RenderIcon } from "@/components/admin/IconPicker";

/**
 * Home "Cu ce te pot ajuta" cards.
 *
 * PRIMARY SOURCE: techniques collection (each gets its own indexable page at /tehnici/{slug}).
 * Each technique with show_on_home != false will be displayed here (ordered by `order` ASC).
 *
 * FALLBACK: legacy sections.services.content.items (for existing data — non-clickable,
 *   links to /contact as before). Visible only if there are no techniques yet.
 */
export default function Services() {
  const { sectionContent } = useSite();
  const c = sectionContent("services");
  const [techniques, setTechniques] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/public/techniques")
      .then((r) => setTechniques(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTechniques([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!c) return null;
  const legacyItems = c.items || [];
  const homeTechniques = techniques.filter((t) => t.show_on_home !== false);
  // Itemii proprii (formatele: ședințe, cursuri, audio…) au prioritate — altfel secțiunea
  // dubla cardurile de tehnici afișate deja de secțiunea „Tehnici" imediat deasupra.
  const useTechniques = loaded && homeTechniques.length > 0 && legacyItems.length === 0;
  // Adapter so the rest of the render stays unified
  const items = useTechniques
    ? homeTechniques.map((t) => ({
        title: t.name,
        desc: t.summary || "",
        icon: t.icon || "Sparkles",
        image_url: t.thumbnail_url || t.image_url || "",
        href: `/tehnici/${t.slug}`,
        cta_label: t.home_cta_label || "Află mai mult",
      }))
    : legacyItems.map((it) => ({
        title: it.title,
        desc: it.desc,
        icon: it.icon,
        image_url: it.image_url,
        href: "/contact",
        cta_label: "Află mai mult",
      }));

  return (
    <section data-testid="services-section" className="py-14 sm:py-20 lg:py-28" style={{ background: "var(--ct-bg)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 lg:mb-14 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-3 sm:mb-4">
            <span className="gold-rule" />
            <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Cum te pot ajuta"}</span>
            <span className="gold-rule" />
          </div>
          <h2 data-el="title" data-field="title" className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{c.title || "Servicii de transformare interioară"}</h2>
          {c.subtitle && <p data-el="subtitle" data-field="subtitle" className="text-sm sm:text-base leading-relaxed mt-3 sm:mt-4" style={{ color: "var(--ct-text-muted)" }}>{c.subtitle}</p>}
        </div>

        {/* MOBILE: horizontal cards (image left ~35%, content right). DESKTOP: vertical 3-col grid. */}
        <div className="flex flex-col gap-3 sm:hidden">
          {items.map((item, i) => (
            <Link
              to={item.href}
              key={i}
              data-testid={`service-card-mobile-${i}`}
              className="card-premium overflow-hidden grid grid-cols-[35%_1fr] gap-0 active:scale-[0.99] transition-transform"
            >
              <div className="relative overflow-hidden" style={{ background: "var(--ct-surface-2)" }}>
                {item.image_url && <img src={item.image_url} data-img={!useTechniques ? `items.${i}.image_url` : undefined} alt={item.title} className="w-full h-full object-cover" />}
                <div className="absolute bottom-2 left-2 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: "var(--ct-emerald)", border: "1.5px solid var(--ct-gold)" }}>
                  <RenderIcon value={item.icon} size={14} color="var(--ct-gold)" />
                </div>
              </div>
              <div className="p-3.5 flex flex-col justify-center">
                <h3 data-el="item_title" data-field={!useTechniques ? `items.${i}.title` : undefined} className="font-serif text-[17px] leading-tight mb-1">{item.title}</h3>
                <p data-el="item_body" data-field={!useTechniques ? `items.${i}.desc` : undefined} className="text-[12px] leading-snug line-clamp-3" style={{ color: "var(--ct-text-muted)" }}>{item.desc}</p>
                <span className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "var(--ct-link)" }}>
                  {item.cta_label} <ChevronRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="hidden sm:grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <Link
              to={item.href}
              key={i}
              data-testid={`service-card-${i}`}
              className="card-premium overflow-hidden flex flex-col h-full hover:-translate-y-0.5 transition-transform"
            >
              {item.image_url && (
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img src={item.image_url} data-img={!useTechniques ? `items.${i}.image_url` : undefined} alt={item.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-8 flex flex-col flex-1 relative">
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 ${item.image_url ? "absolute -top-7 left-7" : ""}`}
                  style={{ background: "var(--ct-emerald)", border: "1.5px solid var(--ct-gold)" }}
                >
                  <RenderIcon value={item.icon} size={20} color="var(--ct-gold)" />
                </div>
                <h3 data-el="item_title" data-field={!useTechniques ? `items.${i}.title` : undefined} className="font-serif text-2xl mb-3 mt-2">{item.title}</h3>
                <p data-el="item_body" data-field={!useTechniques ? `items.${i}.desc` : undefined} className="text-sm leading-relaxed flex-1" style={{ color: "var(--ct-text-muted)" }}>{item.desc}</p>
                <span
                  className="mt-6 inline-flex items-center gap-2 text-sm font-medium hover:gap-3 transition-all"
                  style={{ color: "var(--ct-link)" }}
                  data-testid={`service-cta-${i}`}
                >
                  {item.cta_label} <ChevronRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
